import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { handleComboboxTriggerPointerDown } from '../../../utils/comboboxTrigger';
import './FilterMultiSelect.css';

const normalizeMultiSelectId = (id: string | number) => String(id);

export interface FilterMultiSelectOption<TId extends string | number = string | number> {
  id: TId;
  name: string;
}

export interface FilterMultiSelectProps<TId extends string | number = string | number> {
  variant: 'client' | 'project' | 'report';
  /** Если false — подпись снаружи (например data-label у project-detail-filter-group) */
  showLabel?: boolean;
  label?: string;
  options: FilterMultiSelectOption<TId>[];
  selectedIds: ReadonlySet<TId>;
  onToggle: (id: TId) => void;
  placeholder?: string;
  /** Подпись для одного выбранного элемента, если имя не найдено в options */
  fallbackItemLabel?: string;
}

function FilterMultiSelect<TId extends string | number = string | number>({
  variant,
  showLabel = true,
  label = 'Исполнители',
  options,
  selectedIds,
  onToggle,
  placeholder = 'Выберите из списка',
  fallbackItemLabel = 'Элемент',
}: FilterMultiSelectProps<TId>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [panelBox, setPanelBox] = useState<{ top: number; left: number; width: number } | null>(null);

  const close = useCallback(() => setOpen(false), []);

  const syncPanelPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const pad = 8;
    const width = Math.min(r.width, vw - pad * 2);
    let left = r.left;
    if (left + width > vw - pad) left = Math.max(pad, vw - pad - width);
    setPanelBox({
      top: r.bottom + (variant === 'project' ? 6 : 4),
      left,
      width,
    });
  }, [variant]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    syncPanelPosition();
  }, [open, syncPanelPosition, variant]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => syncPanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, syncPanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open, close]);

  const optionById = useMemo(() => new Map(options.map((o) => [normalizeMultiSelectId(o.id), o])), [options]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.name ?? '').toLowerCase().includes(q));
  }, [options, search]);

  const triggerText = useMemo(() => {
    const n = selectedIds.size;
    if (n === 0) return placeholder;
    if (n === 1) {
      const id = normalizeMultiSelectId(Array.from(selectedIds)[0]);
      const o = optionById.get(id);
      const name = o?.name?.trim();
      return name || `${fallbackItemLabel} #${id}`;
    }
    const names: string[] = [];
    const selectedKeys = new Set(Array.from(selectedIds).map(normalizeMultiSelectId));
    for (const o of options) {
      const key = normalizeMultiSelectId(o.id);
      if (!selectedKeys.has(key)) continue;
      names.push(o.name?.trim() || `${fallbackItemLabel} #${key}`);
    }
    for (const id of Array.from(selectedKeys)) {
      if (optionById.has(id)) continue;
      names.push(`${fallbackItemLabel} #${id}`);
    }
    return names.join(', ');
  }, [selectedIds, optionById, options, placeholder, fallbackItemLabel]);

  const panel = open && panelBox ? (
    <div
      ref={panelRef}
      className={`filter-multiselect-panel filter-multiselect-panel--portal filter-multiselect-panel--${variant}`}
      role="listbox"
      style={{
        position: 'fixed',
        top: panelBox.top,
        left: panelBox.left,
        width: panelBox.width,
        maxWidth: panelBox.width,
        minWidth: panelBox.width,
        boxSizing: 'border-box',
      }}
    >
      {filteredOptions.length === 0 ? (
        <div className="filter-multiselect-empty">Нет вариантов</div>
      ) : (
        filteredOptions.map((u) => {
          const key = normalizeMultiSelectId(u.id);
          return (
            <label key={key} className="filter-multiselect-option">
              <input
                type="checkbox"
                checked={Array.from(selectedIds).some((id) => normalizeMultiSelectId(id) === key)}
                onChange={() => onToggle(u.id)}
              />
              <span className="filter-multiselect-option-text" title={u.name}>
                {u.name || `${fallbackItemLabel} #${key}`}
              </span>
            </label>
          );
        })
      )}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`filter-multiselect filter-multiselect--${variant} ${open ? 'filter-multiselect--open' : ''} ${selectedIds.size === 0 ? 'filter-multiselect--placeholder' : ''}`}
      >
        {showLabel ? <span className="filter-multiselect-visible-label">{label}</span> : null}
        <div
          ref={triggerRef}
          className="filter-multiselect-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          onPointerDown={(e) => handleComboboxTriggerPointerDown(e, {
            inputRef,
            onOpen: () => setOpen(true),
            shouldIgnore: (t) => Boolean(t.closest('.filter-multiselect-chevron')),
          })}
        >
          <input
            ref={inputRef}
            className="filter-multiselect-trigger-input"
            type="text"
            value={open ? search : ''}
            placeholder={triggerText}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              if (!open) setOpen(true);
              setSearch(e.target.value);
            }}
          />
          <span
            className={`filter-multiselect-chevron ${open ? 'filter-multiselect-chevron--open' : ''}`}
            role="button"
            tabIndex={0}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Закрыть список' : 'Открыть список'}
          />
        </div>
      </div>
      {panel && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}

export default FilterMultiSelect;
